import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { projects } from "../data/projects";
import { seoProjects } from "../data/seoProjects";
import Logo from "../assets/images/Rankifydev-logo.png";

const NAV = [
  { label: "Home", href: "/" },

  {
    label: "Services",
    dropdown: [
      {
        label: "Novafy",
        href: "/novafy",
      },
    ],
  },

  { label: "About", href: "/#about" },
  { label: "Process", href: "/#process" },

  {
    label: "Portfolio",
    dropdown: [
      {
        label: "Development Portfolio",
        href: "/portfolio",
        badge: `${projects.length}+`,
      },
      {
        label: "SEO Portfolio",
        href: "/seo-portfolio",
        badge: `${seoProjects.length}+`,
      },
    ],
  },

  { label: "Blog", href: "/blog" },
  { label: "FAQ", href: "/#faq" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);

    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo-link">
          <img
            src={Logo}
            alt="RankifyDev Logo"
            className="w-auto"
            style={{ height: "4em" }}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => {
            if (item.dropdown) {
              return (
                <div key={item.label} className="group relative">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium text-[hsl(var(--brand-navy))] hover:text-brand transition-colors"
                  >
                    {item.label}

                    <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                  </button>

                  <div className="absolute left-0 top-full invisible mt-2 w-60 translate-y-2 rounded-xl border border-black/10 bg-white p-1.5 opacity-0 shadow-lg transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                    {item.dropdown.map((option) => (
                      <Link
                        key={option.label}
                        to={option.href}
                        className="flex items-center justify-between gap-3 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium text-[hsl(var(--brand-navy))] transition hover:bg-lilac hover:text-brand"
                      >
                        {option.label}

                        {option.badge && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[hsl(var(--brand-amber)/.18)] text-amber-brand">
                            {option.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.label}
                to={item.href}
                className="link-underline text-sm font-medium text-[hsl(var(--brand-navy))] hover:text-brand transition-colors flex items-center gap-2"
              >
                {item.label}

                {item.badge && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[hsl(var(--brand-amber)/.18)] text-amber-brand">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/#contact"
            data-testid="header-cta-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-navy))] hover:bg-brand text-white px-5 py-2.5 text-sm font-semibold transition-all duration-300"
          >
            Free Audit

            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          type="button"
          className="lg:hidden p-2 -mr-2 text-[hsl(var(--brand-navy))]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle navigation menu"
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      {open && (
        <div className="lg:hidden bg-white border-t border-black/5">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => {
              if (item.dropdown) {
                return (
                  <React.Fragment key={item.label}>
                    <div className="py-2 text-xs font-bold uppercase text-slate-400">
                      {item.label}
                    </div>

                    {item.dropdown.map((option) => (
                      <Link
                        key={option.label}
                        to={option.href}
                        className="flex items-center justify-between gap-3 py-3 pl-3 text-base font-medium text-[hsl(var(--brand-navy))] hover:text-brand"
                      >
                        {option.label}

                        {option.badge && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[hsl(var(--brand-amber)/.18)] text-amber-brand">
                            {option.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </React.Fragment>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className="block py-3 text-base font-medium text-[hsl(var(--brand-navy))] hover:text-brand"
                >
                  {item.label}
                </Link>
              );
            })}

            <Link
              to="/#contact"
              className="block mt-2 text-center rounded-full bg-brand text-white px-5 py-3 text-sm font-semibold"
            >
              Free Audit
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;